// FIXME: remove when list-tags replaced by labels
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TagBadge, TagKeyOptions, visibleTags } from './ListTagsField';

interface TagsCellProps {
  tags: string[] | undefined;
  options: TagKeyOptions;
}

const TAG_CONFIG = {
  MAX_VISIBLE: 4,
  MIN_VISIBLE_PX: 6,
  WIDTH_BY_COUNT: {
    1: 180,
    2: 180,
    3: 120,
    4: 80,
  } as Record<number, number>,
  OVERLAP_INTENSITY: {
    2: 0.1,
    3: 0.3,
    4: 0.7,
  } as Record<number, number>,
} as const;

const getTagWidth = (total: number): number =>
  TAG_CONFIG.WIDTH_BY_COUNT[total] ?? TAG_CONFIG.WIDTH_BY_COUNT[4];

const getOverlapMargin = (index: number, total: number): number => {
  if (index === 0) return 0;
  if (total === 1) return 0;
  const width = getTagWidth(total);
  const intensity =
    TAG_CONFIG.OVERLAP_INTENSITY[total] ?? TAG_CONFIG.OVERLAP_INTENSITY[4];
  const distanceFromEnd = total - index;
  const overlapFactor = 1 - 1 / (distanceFromEnd + 1);
  const overlapAmount = Math.min(
    width - TAG_CONFIG.MIN_VISIBLE_PX,
    width * overlapFactor * intensity,
  );
  return -overlapAmount;
};

// Department tags first, others after. Stable within each group.
const tagSortRank = (tag: string): number =>
  tag.startsWith('department:') ? 0 : 1;

export function TagsCell({ tags, options }: TagsCellProps) {
  const visible = [...visibleTags(tags ?? [])].sort(
    (a, b) => tagSortRank(a) - tagSortRank(b),
  );
  if (visible.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  const displayed = visible.slice(0, TAG_CONFIG.MAX_VISIBLE);
  const remaining = visible.length - TAG_CONFIG.MAX_VISIBLE;
  const tagWidth = getTagWidth(displayed.length);

  const stacked = (
    <div className="isolate flex min-w-0 flex-1 items-center overflow-visible">
      {displayed.map((tag, index) => (
        <div
          key={tag}
          className="[&>*]:ring-card [&>*]:flex [&>*]:max-w-full [&>*]:border-0 [&>*]:ring-2"
          style={{
            zIndex: index,
            marginLeft: getOverlapMargin(index, displayed.length),
            maxWidth: tagWidth,
          }}
        >
          <TagBadge tag={tag} options={options} compact />
        </div>
      ))}
      {remaining > 0 && (
        <span className="text-muted-foreground ml-1 flex-shrink-0 text-xs">
          +{remaining}
        </span>
      )}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default">{stacked}</div>
      </TooltipTrigger>
      <TooltipContent
        className="bg-popover text-popover-foreground hidden border shadow-md md:block"
        arrowClassName="bg-popover fill-popover"
        align="end"
        sideOffset={4}
      >
        <div className="flex max-w-100 flex-wrap gap-1.5 p-1">
          {visible.map((tag) => (
            <TagBadge key={tag} tag={tag} options={options} />
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
